import React, { useEffect, useMemo, useState } from 'react';
import { App, Button, Form, Input, InputNumber, Select, Switch, Tag, message } from 'antd';
import { useTranslation } from 'react-i18next';
import EditableDrawer from 'components/EditableDrawer';
import { ROLE_OPTIONS } from '../../../../constants/roleOptions';
import { pickPageName, pickPortalName } from '../../../../utils/dataPortalCache';
import AccountPicker from './AccountPicker';
import { DataPortalGroupType, DataPortalPageType } from '../graphql/queries';

interface Props {
    visible: boolean;
    mode: 'create' | 'edit';
    initialValues?: DataPortalPageType | null;
    /** 可选来源门户，取自后端配置。新增门户只加后端配置，这里自动出现选项 */
    portalCodes: string[];
    groups: DataPortalGroupType[];
    /** 同组内已加载的看板，用来本地算「当前分组内排第 N 位」，不额外请求 */
    siblings: DataPortalPageType[];
    onClose: () => void;
    onSubmit: (values: any) => Promise<void> | void;
}

const inputStyle = { width: 340 };

/**
 * 可见角色候选：`ROLE_OPTIONS` 原样，只排掉 `'all'`（它不是角色，是筛选用的「全部」）。
 *
 * 不按登录页黑名单二次过滤（2026-08-06 决策）：PC 端能否登录由 `LoginPage` 的 deniedRoles
 * 统一把关，这里再抄一份就成了第二份事实源，登录规则调整时不会同步、也没人会发现。
 * 移动端角色被配上也无害，它们进不了 desktop-web。
 */
const useVisibleRoleOptions = () =>
    ROLE_OPTIONS.filter((o) => o.value !== 'all').map((o) => ({ value: o.value, label: o.label }));

/**
 * 从粘贴内容里取出嵌入地址。
 *
 * 商分门户的分享弹窗提供两个复制按钮：纯链接，和 `<iframe src="..." ...>` 代码。
 * 管理员两种都可能直接粘过来，所以这里都认——是 iframe 代码就抽 src。
 * 域名白名单与「只存路径」的归一化在后端做（安全校验不能只靠前端）。
 */
function extractEmbedUrl(raw: string): string {
    const input = (raw ?? '').trim();
    const match = input.match(/<iframe[^>]*\ssrc=["']([^"']+)["']/i);
    return match ? match[1].trim() : input;
}

/**
 * 新建 / 编辑看板。
 *
 * 标题用「看板」而不是「嵌入页」，副信息里的「页面标识」改称「看板 ID」——
 * 业务方不理解原来那套技术术语。
 */
const DataPortalPageDrawer: React.FC<Props> = ({
    visible,
    mode,
    initialValues,
    portalCodes,
    groups,
    siblings,
    onClose,
    onSubmit,
}) => {
    const { t, i18n } = useTranslation();
    const { modal } = App.useApp();
    const [form] = Form.useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const visibleScope = Form.useWatch('visibleScope', form);
    const visibleAccountKey = Form.useWatch('visibleAccountKey', form);
    const sortOrder = Form.useWatch('sortOrder', form);
    const groupId = Form.useWatch('groupId', form);
    const portalCode = Form.useWatch('portalCode', form);
    const roleOptions = useVisibleRoleOptions();

    /**
     * 分组下拉只列当前门户的分组。
     *
     * 不过滤的话管理员能选到别门户的分组，保存时才被后端以 PORTAL_GROUP_MISMATCH 拒绝——
     * 错误发生在离操作最远的地方，最难对应回「刚才选错了哪一项」。
     */
    const groupOptions = useMemo(
        () =>
            groups
                .filter((group) => group.portalCode === portalCode)
                .map((group) => ({ value: group.id, label: pickPageName(group, i18n.language) })),
        [groups, portalCode, i18n.language],
    );

    /** 历史配置里存的是手机号，人员选择器只产出账号ID —— 这种记录先只读展示，别静默改写 */
    const legacyPhoneMode = visibleScope === 'ACCOUNT' && visibleAccountKey === 'PHONE';

    useEffect(() => {
        if (!visible) return;
        form.resetFields();
        form.setFieldsValue({
            openMode: 'IFRAME',
            // 默认商分门户
            portalCode: 'BA',
            enabled: true,
            sortOrder: 0,
            groupId: null,
            // 不再有「全部可见」，可见范围不预选，强制配置人显式选按角色/按人员
            visibleScope: undefined,
            restrictConfigVisible: false,
            visibleRoles: [],
            visibleAccountKey: 'ACCOUNT_ID',
            visibleAccounts: [],
            ...initialValues,
            // 存量公开看板（scope=ALL）编辑时把 scope 清空、强制重选：ALL 已不在下拉里，
            // 且后端保存会拒绝 ALL，留着它用户会保存失败却不知道要改哪
            ...(initialValues?.visibleScope === 'ALL' ? { visibleScope: undefined } : {}),
        });
    }, [visible, initialValues, form]);

    /** 「当前分组内排第 N 位」：用已加载的同组看板本地算，规则与后端/浏览页一致（降序，同值按名称） */
    const rankHint = useMemo(() => {
        const current = Number(sortOrder ?? 0);
        const others = siblings.filter(
            (page) => page.id !== initialValues?.id && (page.groupId ?? null) === (groupId ?? null),
        );
        const ahead = others.filter((page) => page.sortOrder > current).length;
        return ahead + 1;
    }, [siblings, sortOrder, groupId, initialValues?.id]);

    const handleSubmit = async () => {
        // 先校验，通过后弹二级确认（提示改动会立即对可见用户生效），确认了才真正提交
        let values: any;
        try {
            values = await form.validateFields();
        } catch (e) {
            console.error('Data portal config form validation failed:', e);
            return;
        }
        // 指定角色/人员时必须至少选一个（后端也会拦，这里先给即时反馈）
        if (values.visibleScope === 'ROLE' && (values.visibleRoles ?? []).length === 0) {
            message.error(t('central.dataPortalConfig.messages.visibleRolesRequired') as string);
            return;
        }
        if (values.visibleScope === 'ACCOUNT' && (values.visibleAccounts ?? []).length === 0) {
            message.error(t('central.dataPortalConfig.messages.visibleAccountsRequired') as string);
            return;
        }
        modal.confirm({
            title: t('central.dataPortalConfig.confirmDialog.title'),
            content: t('central.dataPortalConfig.confirmDialog.board'),
            okText: t('components.button.confirm') as string,
            cancelText: t('components.button.cancel') as string,
            onOk: async () => {
                setIsSubmitting(true);
                try {
                    await onSubmit(values);
                } finally {
                    setIsSubmitting(false);
                }
            },
        });
    };

    /** 三个菜单名称至少填一个 */
    const validateAtLeastOneName = async () => {
        const { nameEn, nameZh, nameEs } = form.getFieldsValue(['nameEn', 'nameZh', 'nameEs']);
        if (!nameEn?.trim() && !nameZh?.trim() && !nameEs?.trim()) {
            throw new Error(t('central.dataPortalConfig.messages.nameAtLeastOne') as string);
        }
    };

    /** 粘贴时若是 iframe 代码就当场抽成纯链接，让管理员看到实际会提交的内容 */
    const handleEmbedPathBlur = () => {
        const current = form.getFieldValue('embedPath');
        const extracted = extractEmbedUrl(current ?? '');
        if (extracted !== current) form.setFieldsValue({ embedPath: extracted });
    };

    const switchToPicker = () => {
        // 明确由管理员点了才清空重填，不做静默转换
        form.setFieldsValue({ visibleAccountKey: 'ACCOUNT_ID', visibleAccounts: [] });
    };

    return (
        <EditableDrawer
            title={mode === 'create'
                ? t('central.dataPortalConfig.drawer.new')
                : t('central.dataPortalConfig.drawer.edit')}
            visible={visible}
            onClose={onClose}
            onSubmit={handleSubmit}
            confirmText={isSubmitting
                ? t('components.button.processing') as string
                : t('components.button.confirm') as string}
            cancelText={t('components.button.cancel') as string}
            confirmDisabled={isSubmitting}
            width={680}
        >
            <Form form={form} layout="horizontal" labelAlign="left" labelCol={{ span: 7 }} wrapperCol={{ span: 17 }}>
                {/* 看板 ID 由后端生成，配置人员不需要填；编辑时只读展示，便于对照 URL */}
                {mode === 'edit' && (
                    <Form.Item label={t('central.dataPortalConfig.fields.boardId')}>
                        <span className="text-gray-500">{initialValues?.pageKey ?? '-'}</span>
                    </Form.Item>
                )}

                <Form.Item
                    name="portalCode"
                    label={t('central.dataPortalConfig.fields.portalCode')}
                    tooltip={t('central.dataPortalConfig.tips.portalCode')}
                    rules={[{ required: true }]}
                >
                    <Select
                        style={inputStyle}
                        size="middle"
                        options={portalCodes.map((code) => ({
                            value: code,
                            // pickPortalName 内部带 defaultValue 兜底——新增门户忘配翻译也不会显示空白
                            label: pickPortalName(code, t),
                        }))}
                        // 切门户必须清掉已选分组：分组绑定唯一门户，留着旧分组会被后端
                        // 以 PORTAL_GROUP_MISMATCH 拒绝，而管理员看不出是哪里不对
                        onChange={() => form.setFieldValue('groupId', null)}
                    />
                </Form.Item>

                <Form.Item
                    name="embedPath"
                    label={t('central.dataPortalConfig.fields.embedPath')}
                    tooltip={t('central.dataPortalConfig.tips.embedPath')}
                    extra={t('central.dataPortalConfig.tips.embedPathExtra')}
                    rules={[{ required: true, message: t('central.dataPortalConfig.messages.embedPathRequired') as string }]}
                >
                    <Input.TextArea
                        rows={3}
                        style={inputStyle}
                        onBlur={handleEmbedPathBlur}
                        placeholder="https://ba.prod.tita.ltd/share/xxxx/embed"
                    />
                </Form.Item>

                {/* 三语至少填一个即可；展示时按 en > zh > es 回退（见 utils/dataPortalCache 的 pickPageName）。
                    校验挂在中文那一项上并依赖另两项，这样只出一条错误提示，不会三处同时报红。 */}
                <Form.Item
                    name="nameZh"
                    label={t('central.dataPortalConfig.fields.nameZh')}
                    tooltip={t('central.dataPortalConfig.tips.menuName')}
                    dependencies={['nameEn', 'nameEs']}
                    rules={[{ validator: validateAtLeastOneName }]}
                >
                    <Input style={inputStyle} size="middle" />
                </Form.Item>
                <Form.Item name="nameEn" label={t('central.dataPortalConfig.fields.nameEn')}>
                    <Input style={inputStyle} size="middle" />
                </Form.Item>
                <Form.Item name="nameEs" label={t('central.dataPortalConfig.fields.nameEs')}>
                    <Input style={inputStyle} size="middle" />
                </Form.Item>

                <Form.Item name="groupId" label={t('central.dataPortalConfig.fields.group')}>
                    <Select
                        style={inputStyle}
                        size="middle"
                        allowClear
                        showSearch
                        // 按分组名模糊匹配；optionFilterProp=label 让搜索匹配展示文案而非 value(id)
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                            (option?.label as string).toLowerCase().includes(input.trim().toLowerCase())
                        }
                        placeholder={t('pages.dataPortal.ungrouped')}
                        options={groupOptions}
                    />
                </Form.Item>

                <Form.Item
                    label={t('central.dataPortalConfig.fields.visibleScope')}
                    tooltip={t('central.dataPortalConfig.tips.visibleScope')}
                    required
                >
                    <div className="flex flex-col gap-2" style={inputStyle}>
                        <Form.Item
                            name="visibleScope"
                            noStyle
                            rules={[{ required: true, message: t('central.dataPortalConfig.messages.visibleScopeRequired') as string }]}
                        >
                            <Select
                                size="middle"
                                placeholder={t('central.dataPortalConfig.placeholders.visibleScope')}
                                options={[
                                    { value: 'ROLE', label: t('central.dataPortalConfig.visibleScope.role') },
                                    { value: 'ACCOUNT', label: t('central.dataPortalConfig.visibleScope.account') },
                                ]}
                            />
                        </Form.Item>

                        {visibleScope === 'ACCOUNT' ? (
                            legacyPhoneMode ? (
                                <div className="flex flex-col gap-2">
                                    <div className="text-xs text-amber-600">
                                        {t('central.dataPortalConfig.accountPicker.legacyPhoneTip')}
                                    </div>
                                    <Form.Item name="visibleAccounts" noStyle>
                                        <PhoneTagList />
                                    </Form.Item>
                                    <Button size="small" onClick={switchToPicker}>
                                        {t('central.dataPortalConfig.accountPicker.switchToPicker')}
                                    </Button>
                                </div>
                            ) : (
                                <Form.Item name="visibleAccounts" noStyle>
                                    <AccountPicker />
                                </Form.Item>
                            )
                        ) : visibleScope === 'ROLE' ? (
                            <Form.Item name="visibleRoles" noStyle>
                                <Select
                                    size="middle"
                                    mode="multiple"
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder={t('central.dataPortalConfig.placeholders.visibleRoles')}
                                    options={roleOptions}
                                />
                            </Form.Item>
                        ) : null}

                        {/* 限制配置人员可见：选了按角色/按人员后才出现。关=配置人员照旧旁路可见；开=严格按上面范围 */}
                        {(visibleScope === 'ROLE' || visibleScope === 'ACCOUNT') && (
                            <div className="flex items-center justify-between mt-1 pt-2 border-t border-gray-100">
                                <span className="text-sm text-gray-600">
                                    {t('central.dataPortalConfig.fields.restrictConfigVisible')}
                                    <span className="ml-1 text-xs text-gray-400">
                                        {t('central.dataPortalConfig.tips.restrictConfigVisible')}
                                    </span>
                                </span>
                                <Form.Item name="restrictConfigVisible" noStyle valuePropName="checked">
                                    <Switch size="small" />
                                </Form.Item>
                            </div>
                        )}
                    </div>
                </Form.Item>

                <Form.Item name="icon" label={t('central.dataPortalConfig.fields.icon')} tooltip={t('central.dataPortalConfig.tips.icon')}>
                    <Input style={inputStyle} size="middle" />
                </Form.Item>

                <Form.Item
                    name="sortOrder"
                    label={t('central.dataPortalConfig.fields.sortOrder')}
                    extra={t('central.dataPortalConfig.tips.sortOrder', { rank: rankHint })}
                >
                    <InputNumber min={0} max={99999} precision={0} style={inputStyle} size="middle" />
                </Form.Item>

                <Form.Item name="enabled" label={t('central.dataPortalConfig.fields.enabled')} valuePropName="checked">
                    <Switch />
                </Form.Item>
            </Form>
        </EditableDrawer>
    );
};

/** 历史手机号配置的只读展示。不给编辑入口，避免和「只产出账号ID」的选择器混用出两种语义 */
const PhoneTagList: React.FC<{ value?: string[] }> = ({ value = [] }) => (
    <div className="flex flex-wrap gap-1">
        {value.map((phone) => (
            <Tag key={phone}>{phone}</Tag>
        ))}
    </div>
);

export default DataPortalPageDrawer;
